import { reactive, ref } from "vue";

import type { Master, MasterInput } from "@/entities/master";
import { masterApi } from "@/entities/master";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib";

import type { MasterErrorField, MasterErrorKey } from "./mapMasterError";
import { mapMasterError } from "./mapMasterError";

/** Стан завантаження профілю для edit-режиму (spec §5.2). */
export type LoadStatus = "idle" | "loading" | "ready" | "notFound" | "error";
/** Стан сабміту форми. */
export type SubmitStatus = "idle" | "saving";

/** Поля, що можуть нести інлайн/form-level помилку. */
export type FieldKey = keyof MasterInput;
/** `form` — form-level помилка над групою ПІБ (422 `invalid_person_name`). */
export type FieldErrors = Partial<Record<FieldKey | "form", MasterErrorKey>>;

/** Колбеки результату — навігацію/announce робить сторінка (spec §4.3/§6). */
export interface MasterProfileFormCallbacks {
  onCreated?: (master: Master) => void;
  onSaved?: (master: Master) => void;
  onNotFound?: () => void;
}

export interface UseMasterProfileFormOptions extends MasterProfileFormCallbacks {
  mode: "create" | "edit";
  /** Обовʼязковий для `mode==='edit'`. */
  id?: string;
}

const NAME_MAX = 100;
const EMAIL_MAX = 254;
const PHONE_MAX = 32;
/** Базова перевірка форми email (бекенд — істина; це лише ранній UX-guard). */
const EMAIL_RE = /^\S+@\S+\.\S+$/;
/** Порядок полів — для фокуса на 1-ше invalid (spec §8.3). */
const FIELD_ORDER: FieldKey[] = ["firstName", "lastName", "middleName", "email", "phone"];

function tr(key: string, named?: Record<string, unknown>): string {
  return named ? i18n.global.t(key, named) : i18n.global.t(key);
}

function emptyInput(): MasterInput {
  return { firstName: "", lastName: "", middleName: "", email: "", phone: "" };
}

/**
 * Екранний стан форми профілю майстра (spec §5.2). **Composable, не store** — щоб
 * кожен маунт сторінки (`/new` ↔ кожен `/:id`) давав чистий інстанс без
 * Pinia-singleton-reset. Тримає поля, помилки, статуси завантаження/сабміту/
 * видалення; навігацію не робить — повертає результат через колбеки (`onCreated`/
 * `onSaved`/`onNotFound`), сторінка вирішує роутинг і announce.
 */
export function useMasterProfileForm(opts: UseMasterProfileFormOptions) {
  const loadStatus = ref<LoadStatus>("idle");
  const submitStatus = ref<SubmitStatus>("idle");
  const master = ref<Master | null>(null);
  const fields = reactive<MasterInput>(emptyInput());
  const fieldErrors = reactive<FieldErrors>({});
  /** Перше invalid-поле після `validate()` — сторінка/форма фокусує його. */
  const firstInvalidField = ref<FieldKey | null>(null);

  function clearErrors(): void {
    for (const k of Object.keys(fieldErrors) as (keyof FieldErrors)[]) {
      delete fieldErrors[k];
    }
    firstInvalidField.value = null;
  }

  function seedFrom(m: Master | null): void {
    const src = m ?? emptyInput();
    fields.firstName = src.firstName;
    fields.lastName = src.lastName;
    fields.middleName = src.middleName;
    fields.email = src.email;
    fields.phone = src.phone;
  }

  /** Скинути форму у чистий create-стан (spec §5.2). */
  function initCreate(): void {
    seedFrom(null);
    clearErrors();
    master.value = null;
    loadStatus.value = "ready";
  }

  /**
   * Завантажити майстра для edit (spec §5.2). 404 `master_not_found` / 400
   * `invalid_id` → `notFound` (панель + `onNotFound`); network/server → `error`
   * (панель + retry).
   */
  async function loadForEdit(targetId: string): Promise<void> {
    loadStatus.value = "loading";
    clearErrors();
    try {
      const m = await masterApi.getMaster(targetId);
      master.value = m;
      seedFrom(m);
      loadStatus.value = "ready";
    } catch (e) {
      const mapped = mapMasterError(e as AppError, "load");
      if (mapped.surface === "notFound") {
        loadStatus.value = "notFound";
        opts.onNotFound?.();
      } else {
        loadStatus.value = "error";
      }
    }
  }

  /**
   * Client-валідація (spec §7.4): required `firstName`/`lastName`, length-guards
   * per-поле, базовий формат email і довжина phone. Запит не шлеться. Заповнює
   * `fieldErrors` + `firstInvalidField`; повертає чи форма валідна.
   */
  function validate(): boolean {
    clearErrors();
    const fn = fields.firstName.trim();
    const ln = fields.lastName.trim();
    const mn = fields.middleName.trim();
    const em = fields.email.trim();
    const ph = fields.phone.trim();

    if (!fn) {
      fieldErrors.firstName = "masters.errors.firstNameRequired";
    } else if (fn.length > NAME_MAX) {
      fieldErrors.firstName = "masters.errors.nameTooLong";
    }
    if (!ln) {
      fieldErrors.lastName = "masters.errors.lastNameRequired";
    } else if (ln.length > NAME_MAX) {
      fieldErrors.lastName = "masters.errors.nameTooLong";
    }
    if (mn.length > NAME_MAX) {
      fieldErrors.middleName = "masters.errors.nameTooLong";
    }
    if (em && (em.length > EMAIL_MAX || !EMAIL_RE.test(em))) {
      fieldErrors.email = "masters.errors.invalidEmail";
    }
    if (ph && ph.length > PHONE_MAX) {
      fieldErrors.phone = "masters.errors.invalidPhone";
    }

    firstInvalidField.value = FIELD_ORDER.find((f) => fieldErrors[f]) ?? null;
    return firstInvalidField.value === null;
  }

  /** Застосувати серверну помилку сабміту (create/update) до UI (spec §7.4). */
  function applySubmitError(e: AppError, ctx: "create" | "update"): void {
    const mapped = mapMasterError(e, ctx);
    switch (mapped.surface) {
      case "field":
        fieldErrors[mapped.field as MasterErrorField] = mapped.i18nKey;
        firstInvalidField.value = mapped.field as FieldKey;
        break;
      case "formName":
        fieldErrors.form = mapped.i18nKey;
        firstInvalidField.value = "firstName";
        break;
      case "gone":
        // PATCH 404: майстра вже немає (конкурентне видалення) → toast + notFound.
        toast.info(tr(mapped.i18nKey));
        opts.onNotFound?.();
        break;
      default:
        if (mapped.toastVariant === "info") {
          toast.info(tr(mapped.i18nKey));
        } else {
          toast.error(tr(mapped.i18nKey));
        }
    }
  }

  /**
   * Сабміт форми (spec §5.2/§7.3). Спершу client-валідація: невалідно → `false`
   * (форма фокусує `firstInvalidField`), **запит не йде**. Валідно → create|update
   * (server-confirmed); успіх → `onCreated`/`onSaved` (edit пере-сіється з
   * відповіді = серверна істина); помилка → `applySubmitError`. Повертає `false`
   * лише на client-блок валідації.
   */
  async function submit(): Promise<boolean> {
    // Guard від подвійного сабміту: Enter в інпуті тригерить @submit.prevent повз
    // дизейблнуту кнопку → інакше два паралельні POST/PATCH.
    if (submitStatus.value === "saving") {
      return false;
    }
    if (!validate()) {
      return false;
    }
    submitStatus.value = "saving";
    try {
      if (opts.mode === "create") {
        const created = await masterApi.createMaster(fields);
        opts.onCreated?.(created);
      } else {
        if (!opts.id) {
          return false;
        }
        const saved = await masterApi.updateMaster(opts.id, fields);
        master.value = saved;
        seedFrom(saved);
        opts.onSaved?.(saved);
      }
    } catch (e) {
      applySubmitError(e as AppError, opts.mode === "create" ? "create" : "update");
    } finally {
      submitStatus.value = "idle";
    }
    return true;
  }

  return {
    loadStatus,
    submitStatus,
    master,
    fields,
    fieldErrors,
    firstInvalidField,
    initCreate,
    loadForEdit,
    validate,
    submit,
  };
}

export type MasterProfileForm = ReturnType<typeof useMasterProfileForm>;
